import React from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import PricingPackage from './packages/PricingPackage';
import BackgroundComposer from './background/BackgroundComposer';
import { cn } from '../utils/cn';
import SectionLabel from './common/SectionLabel';

const packages = [
  {
    title: 'Starter Package',
    description: 'Perfect for small businesses or personal brands launching their first website.',
    features: [
      'Timeline: 1-2 Weeks',
      '1-5 Pages',
      'Custom Website Design',
      'Mobile & Desktop Responsive Design',
      'Basic Contact Form Integration',
      '1 Revision Round',
      '1 Month of Post-Launch Support',
      'Email & Phone Support'
    ],
    actionLabel: 'Start Your Project'
  },
  {
    title: 'Growth Package',
    description: 'Ideal for growing businesses seeking to enhance their online presence.',
    popular: true,
    features: [
      'Timeline: 3-5 Weeks',
      '5-10 Pages',
      'Advanced Custom Website Design',
      'Mobile & Desktop Responsive Design',
      'SEO-Optimized Pages',
      '2 Revision Rounds',
      '3 Months of Post-Launch Support',
      'Priority Support'
    ],
    actionLabel: 'Grow with Us'
  },
  {
    title: 'Premium Package',
    description: 'For established businesses needing a sophisticated website with advanced features.',
    features: [
      'Timeline: 8-11 Weeks',
      '11+ Pages',
      'Fully Customized Design & Development',
      'Advanced Features Integration',
      'SEO-Optimized Content & Analytics',
      '3+ Revision Rounds',
      'Ongoing Support Plan',
      'Priority Support'
    ],
    actionLabel: 'Schedule Consultation'
  }
];

const WebsitePackages = () => {
  return (
    <section id="packages" className="relative overflow-hidden bg-black">
      <BackgroundComposer>
        <div className="container relative z-10 mx-auto px-4">
          <div className="mb-16 max-w-3xl">
            <SectionLabel>Packages</SectionLabel>
            <h2 className="mt-6 text-4xl text-white md:text-5xl">Website Packages</h2>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-[#d0d0d0] md:text-base">
              Tailored solutions to establish and enhance your online presence. Choose the
              package that best fits your business needs and goals.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {packages.map((pkg, index) => (
              <div key={index} className="relative pt-6">
                {pkg.popular && (
                  <motion.div 
                    initial={{ y: -10, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.2 }}
                    className="absolute -top-1 inset-x-0 flex justify-center z-10"
                  >
                    <span className="bg-primary-500 text-white px-6 py-2 rounded-full text-sm font-medium shadow-lg border border-primary-400/20">
                      Most Popular
                    </span>
                  </motion.div>
                )}

                <motion.div
                  whileHover={{ y: -8, scale: 1.02 }}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className={cn(
                    "group relative p-8 rounded-3xl overflow-hidden",
                    "bg-[#111111]",
                    "backdrop-blur-sm",
                    "transition-all duration-500",
                    "shadow-lg shadow-primary-900/10",
                    "hover:shadow-xl hover:shadow-primary-900/20",
                    "min-h-[750px] flex flex-col",
                    pkg.popular ? "border-2 border-primary-500" : "border border-primary-500/20"
                  )}
                >
                {/* Enhanced background glow effect */}
                <div className={cn(
                  "absolute inset-0 opacity-0 group-hover:opacity-100",
                  "bg-gradient-to-br from-primary-500/10 via-transparent to-transparent",
                  "transition-opacity duration-500"
                )} />

                <div className="relative flex flex-col justify-between h-full space-y-8 z-10">
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-2">{pkg.title}</h3>
                    <p className="text-gray-300 mb-8">{pkg.description}</p>
                    <ul className="space-y-4">
                      {pkg.features.map((feature, i) => (
                        <li key={i} className="flex items-center text-gray-300">
                          <Check className="w-5 h-5 text-primary-500 mr-3" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <motion.button
                    className={cn(
                      "w-full py-4 rounded-full text-center mt-auto",
                      "transform transition-all duration-300",
                      "hover:scale-105 active:scale-95",
                      "relative z-10",
                      pkg.popular
                        ? "bg-primary-500 text-white hover:bg-primary-600 shadow-lg shadow-primary-500/20"
                        : "bg-[#161918]/50 text-white border border-primary-500/20 hover:border-primary-500/40"
                    )}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {pkg.actionLabel}
                  </motion.button>
                </div>
                </motion.div>
              </div>
            ))}
          </div>

          <motion.div 
            className="mt-16 text-center"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <a 
              href="#contact" 
              className={cn(
                "inline-block text-primary-400 hover:text-primary-300",
                "transition-colors duration-300",
                "rounded-full px-6 py-3",
                "bg-primary-500/10 hover:bg-primary-500/20 border border-primary-500/20"
              )}
            >
              Need a custom solution? Let's discuss your requirements
            </a>
          </motion.div>
        </div>
      </BackgroundComposer>
    </section>
  );
};

export default WebsitePackages;
